import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArticleCard } from '../components/ArticleCard';
import { articlesAPI } from '../services/api';

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams]);

  const performSearch = async (query) => {
    if (query.trim()) {
      setLoading(true);
      try {
        const searchResults = await articlesAPI.search(query);
        setResults(searchResults);
        setHasSearched(true);
      } catch (error) {
        console.error('Error searching:', error);
        setResults([]);
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
      performSearch(searchQuery);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Search Header */}
      <section className="bg-gradient-to-b from-teal-50 to-white py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 text-center">
            Buscar Artículos
          </h1>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="relative">
            <Input
              type="text"
              placeholder="Buscar por título, contenido o etiquetas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-24 text-lg border-2 border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 rounded-xl"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400" />
            <Button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-teal-500 hover:bg-teal-600 text-white h-10"
              disabled={loading}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </form>
        </div>
      </section>

      {/* Search Results */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Buscando...</p>
            </div>
          ) : hasSearched ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {results.length > 0 ? (
                    <>
                      {results.length} {results.length === 1 ? 'resultado' : 'resultados'} para{' '}
                      <span className="text-teal-600">"{searchParams.get('q')}"</span>
                    </>
                  ) : (
                    <>
                      No se encontraron resultados para{' '}
                      <span className="text-teal-600">"{searchParams.get('q')}"</span>
                    </>
                  )}
                </h2>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {results.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    No se encontraron artículos
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Intenta con otros términos de búsqueda o explora nuestras categorías
                  </p>
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchParams({});
                      setHasSearched(false);
                      setResults([]);
                    }}
                    variant="outline"
                    className="hover:bg-teal-50 hover:text-teal-600 hover:border-teal-500 transition-colors"
                  >
                    Limpiar búsqueda
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                Escribe algo en el buscador para encontrar artículos
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
